import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { turningPointLabel } from '@/lib/narrative-labels';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import PageHeader from '@/components/common/PageHeader';
import { SceneTable } from './SceneTable';
import { CharacterList } from './CharacterList';
import { LocationList } from './LocationList';
import {
  useScenes, useCharacters, useLocations, useAnalysisSummary,
  useNarrativeAnalysis, useCharacterArcs, useEmotionalMap,
  useCastingProfiles, useBudgetEstimate, useDistributionStrategy,
  useThemes, useDiagnostics,
} from '@/hooks/useScriptAnalysis';

interface AnalysisResultsProps {
  jobId: string;
  onNewAnalysis: () => void;
  defaultTab?: string;
}

function SummaryCard({ value, label }: { value: number | string; label: string }) {
  return (
    <Card>
      <CardContent className="p-6">
        <p className="text-xl font-semibold">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

// === Narrative Tab ===
function NarrativeTab({ jobId }: { jobId: string }) {
  const { data: narrative } = useNarrativeAnalysis(jobId);
  const { data: arcs } = useCharacterArcs(jobId);
  const { data: emotions } = useEmotionalMap(jobId);
  const { data: themes } = useThemes(jobId);
  const { data: diagnostics } = useDiagnostics(jobId);
  const { data: casting } = useCastingProfiles(jobId);

  if (!narrative) return <p className="text-muted-foreground py-8 text-center">Análisis narrativo no disponible</p>;

  return (
    <div className="space-y-6">
      {/* Structure */}
      <Card>
        <CardHeader><CardTitle>Estructura Dramática</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><span className="text-sm text-muted-foreground">Paradigma</span><p className="font-medium">{narrative.paradigm?.replace('_', ' ')}</p></div>
            <div><span className="text-sm text-muted-foreground">Género</span><p className="font-medium">{narrative.genre}</p></div>
            <div><span className="text-sm text-muted-foreground">Tono</span><p className="font-medium text-sm">{narrative.tone}</p></div>
            <div><span className="text-sm text-muted-foreground">Audiencia</span><p className="font-medium text-sm">{narrative.audience_estimate?.substring(0, 80)}</p></div>
          </div>
          {narrative.subgenres?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {narrative.subgenres.map((s: string) => <Badge key={s} variant="secondary">{s}</Badge>)}
            </div>
          )}
          {narrative.turning_points?.length > 0 && (
            <div>
              <h4 className="font-medium mb-2">Puntos de Giro</h4>
              <div className="space-y-1">
                {narrative.turning_points.map((tp: any) => (
                  <div key={tp.scene_number} className="flex gap-3 text-sm">
                    <Badge variant="outline" className="shrink-0">Esc {tp.scene_number}</Badge>
                    <span className="text-muted-foreground capitalize">{turningPointLabel(tp.type)}</span>
                    <span>{tp.description?.substring(0, 80)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Character Arcs */}
      {arcs && arcs.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Arcos de Personaje</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {arcs.map((arc: any) => (
                <div key={arc.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{arc.name}</h4>
                    <Badge>{Math.round(arc.presence_pct)}% presencia</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className="text-muted-foreground block">Objetivo</span>{arc.objective?.substring(0, 80)}</div>
                    <div><span className="text-muted-foreground block">Obstáculo</span>{arc.obstacle?.substring(0, 80)}</div>
                    <div><span className="text-muted-foreground block">Transformación</span>{arc.transformation?.substring(0, 80)}</div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
                    <span>Entrada: esc {arc.entry_scene}</span>
                    <span>Clave: esc {arc.key_scene}</span>
                    <span>Salida: esc {arc.exit_scene}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Emotional Map */}
      {emotions && emotions.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Mapa Emocional</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {emotions.map((e: any) => (
                <div key={e.id} className="flex items-center gap-3 text-sm">
                  <span className="w-10 text-right text-muted-foreground">Esc {e.scene_number}</span>
                  <div className="w-28 bg-muted rounded-full h-3 overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full transition-all"
                      style={{ width: `${e.intensity * 10}%` }}
                    />
                  </div>
                  <span className="w-6 text-xs">{e.intensity}</span>
                  <span className="w-32">{e.emotion}</span>
                  <Badge variant="outline" className="text-xs">{e.rhythm}</Badge>
                  <Badge variant={e.change === 'escalation' ? 'default' : e.change === 'descent' ? 'secondary' : 'destructive'} className="text-xs">
                    {e.change}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Themes */}
      {themes && themes.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Temáticas</CardTitle></CardHeader>
          <CardContent>
            {themes.map((t: any) => (
              <div key={t.id} className="mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant={t.type === 'main' ? 'default' : 'secondary'}>{t.type}</Badge>
                  <span className="font-medium">{t.name}</span>
                </div>
                <p className="text-sm text-muted-foreground ml-16">{t.description}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Diagnostics */}
      {diagnostics && diagnostics.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Diagnóstico Narrativo</CardTitle></CardHeader>
          <CardContent>
            {diagnostics.map((d: any) => (
              <div key={d.id} className="mb-3 flex gap-3">
                <Badge variant={d.type === 'strength' ? 'default' : 'destructive'}>
                  {d.type === 'strength' ? '✓' : '✗'} {d.severity}
                </Badge>
                <div>
                  <p className="text-sm">{d.description}</p>
                  {d.suggestion && <p className="text-xs text-muted-foreground mt-1">Sugerencia: {d.suggestion}</p>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Casting */}
      {casting && casting.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Fichas de Casting</CardTitle></CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {casting.map((c: any) => (
                <div key={c.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{c.character_name}</h4>
                    <Badge variant="outline">{c.acting_level?.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="text-sm space-y-1">
                    <p>{c.age_min}-{c.age_max} años, {c.gender} · {c.shooting_days} días rodaje</p>
                    {c.skills?.length > 0 && <p className="text-muted-foreground">Skills: {c.skills.join(', ')}</p>}
                    {c.reference_profile && <p className="text-muted-foreground italic">Ref: {c.reference_profile}</p>}
                    {c.complexity_flags?.length > 0 && (
                      <div className="flex gap-1 flex-wrap mt-1">
                        {c.complexity_flags.map((f: string) => <Badge key={f} variant="destructive" className="text-xs">{f}</Badge>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// === Budget Tab ===
function BudgetTab({ jobId }: { jobId: string }) {
  const { data: budget } = useBudgetEstimate(jobId);

  if (!budget) return <p className="text-muted-foreground py-8 text-center">Presupuesto no disponible</p>;

  const chapters: Record<string, string> = {
    '01': 'Guión/Música', '02': 'Personal Artístico', '03': 'Equipo Técnico',
    '04': 'Escenografía', '05': 'Estudios', '06': 'Maquinaria/Transportes',
    '07': 'Viajes/Dietas', '08': 'Soportes', '09': 'Laboratorio/VFX',
    '10': 'Seguros', '11': 'Gastos Generales', '12': 'P&A',
  };

  const ct = budget.chapter_totals || {};
  const lines = budget.lines || [];
  const total = Object.values(ct).reduce((s: number, v: any) => s + Number(v), 0);
  const flags: string[] = Array.isArray(budget.post_complexity_flags) ? budget.post_complexity_flags : [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard value={`${budget.shooting_days} días`} label="Rodaje" />
        <SummaryCard value={`${budget.shooting_weeks} sem`} label="Semanas rodaje" />
        <SummaryCard value={`${budget.pre_weeks} sem`} label="Pre-producción" />
        <SummaryCard value={`${budget.post_weeks} sem`} label="Post-producción" />
      </div>

      {flags.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-muted-foreground uppercase tracking-wider">
              Factores que elevan el presupuesto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {flags.map((f, i) => (
                <Badge key={i} variant="outline" className="text-xs font-normal">
                  {f}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Presupuesto ICAA</span>
            <span className="text-2xl">{total.toLocaleString('es-ES')}€</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {Object.keys(ct).sort().map(k => {
              const val = Number(ct[k]);
              const pct = total > 0 ? (val / total * 100) : 0;
              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="w-8 text-sm text-muted-foreground">Cap {k}</span>
                  <span className="w-40 text-sm">{chapters[k] || k}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-24 text-right text-sm font-medium">{val.toLocaleString('es-ES')}€</span>
                  <span className="w-10 text-right text-xs text-muted-foreground">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Detalle ({lines.length} líneas)</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Cap</th>
                  <th className="text-left py-2">Puesto</th>
                  <th className="text-left py-2">Tipo</th>
                  <th className="text-right py-2">PRE</th>
                  <th className="text-right py-2">ROD</th>
                  <th className="text-right py-2">POST</th>
                  <th className="text-right py-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {lines.sort((a: any, b: any) => Number(b.total) - Number(a.total)).map((l: any, i: number) => (
                  <tr key={i} className="border-b border-muted">
                    <td className="py-1.5 text-muted-foreground">{l.chapter}</td>
                    <td className="py-1.5">{l.role}</td>
                    <td className="py-1.5"><Badge variant="outline" className="text-xs">{l.billing_type === 'flat_fee' ? 'T/A' : l.billing_type}</Badge></td>
                    <td className="py-1.5 text-right">{l.pre_weeks || '-'}</td>
                    <td className="py-1.5 text-right">{l.shooting_weeks || '-'}</td>
                    <td className="py-1.5 text-right">{l.post_weeks || '-'}</td>
                    <td className="py-1.5 text-right font-medium">{Number(l.total).toLocaleString('es-ES')}€</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// === Distribution Tab ===
function DistributionTab({ jobId }: { jobId: string }) {
  const { data: dist } = useDistributionStrategy(jobId);

  if (!dist) return <p className="text-muted-foreground py-8 text-center">Estrategia de distribución no disponible</p>;

  const pos = dist.positioning || {};
  const tierColors: Record<string, string> = { premiere: 'default', main_circuit: 'secondary', long_run: 'outline' };

  return (
    <div className="space-y-6">
      {/* Positioning */}
      <Card>
        <CardHeader><CardTitle>Posicionamiento</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <blockquote className="border-l-4 border-primary pl-4 italic text-lg">{pos.claim}</blockquote>
          <p className="text-sm">{pos.elevator_pitch}</p>
          {pos.keywords?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {pos.keywords.map((k: string) => <Badge key={k} variant="secondary">{k}</Badge>)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audiences */}
      {dist.audiences?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Audiencias</CardTitle></CardHeader>
          <CardContent>
            {dist.audiences.map((a: any, i: number) => (
              <div key={i} className="mb-4 border rounded-lg p-4">
                <Badge className="mb-2">{a.type}</Badge>
                <p className="text-sm font-medium">{a.demographic}</p>
                <p className="text-sm text-muted-foreground">{a.motivation}</p>
                <p className="text-xs text-muted-foreground mt-1">Canales: {a.channels?.join(', ')}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Festivals */}
      {dist.festivals?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Festivales ({dist.festivals.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dist.festivals.map((f: any, i: number) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  <Badge variant={tierColors[f.tier] as any || 'outline'} className="w-28 justify-center">{f.tier.replace(/_/g, ' ')}</Badge>
                  <span className="w-64 font-medium">{f.name}</span>
                  <span className="text-muted-foreground">{f.section}</span>
                  <span className="ml-auto text-muted-foreground">{f.affinity_pct}% afinidad</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Windows */}
      {dist.windows?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Ventanas de Explotación</CardTitle></CardHeader>
          <CardContent>
            {dist.windows.map((w: any, i: number) => (
              <div key={i} className="flex gap-4 mb-2 text-sm">
                <span className="w-40 font-medium">{w.window}</span>
                <Badge variant="outline">{w.timeline_months} meses</Badge>
                <span className="text-muted-foreground">{w.details?.substring(0, 80)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// === Main Results Component ===
// Map section routes to tab values
const sectionToTab: Record<string, string> = {
  'narrativa-estructura': 'narrativa',
  'narrativa-personajes': 'narrativa',
  'narrativa-emociones': 'narrativa',
  'narrativa-diagnostico': 'narrativa',
  'produccion-escenas': 'escenas',
  'produccion-desglose': 'escenas',
  'produccion-localizaciones': 'localizaciones',
  'produccion-casting': 'narrativa',
  'presupuesto': 'presupuesto',
  'distribucion-posicionamiento': 'distribucion',
  'distribucion-festivales': 'distribucion',
  'distribucion-audiencias': 'distribucion',
};

export function AnalysisResults({ jobId, onNewAnalysis, defaultTab }: AnalysisResultsProps) {
  const { data: scenes, isLoading: scenesLoading } = useScenes(jobId);
  const { data: characters, isLoading: charsLoading } = useCharacters(jobId);
  const { data: locations, isLoading: locsLoading } = useLocations(jobId);
  const summary = useAnalysisSummary(jobId);

  const isLoading = scenesLoading || charsLoading || locsLoading;

  return (
    <div className="max-w-[1200px] mx-auto space-y-8">
      <PageHeader
        title="Análisis de Guión"
        actions={<Button variant="outline" onClick={onNewAnalysis}>Nuevo análisis</Button>}
      />

      {isLoading || !summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-6 space-y-2"><Skeleton className="h-7 w-12" /><Skeleton className="h-4 w-20" /></CardContent></Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <SummaryCard value={summary.total_scenes} label="Escenas" />
          <SummaryCard value={summary.total_characters} label="Personajes" />
          <SummaryCard value={summary.total_locations} label="Localizaciones" />
          <SummaryCard value={summary.total_pages} label="Páginas" />
        </div>
      )}

      <Tabs defaultValue={defaultTab ? (sectionToTab[defaultTab] || defaultTab) : "escenas"}>
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="escenas">Escenas</TabsTrigger>
          <TabsTrigger value="personajes">Personajes</TabsTrigger>
          <TabsTrigger value="localizaciones">Localizaciones</TabsTrigger>
          <TabsTrigger value="narrativa">Narrativa</TabsTrigger>
          <TabsTrigger value="presupuesto">Presupuesto</TabsTrigger>
          <TabsTrigger value="distribucion">Distribución</TabsTrigger>
        </TabsList>

        <TabsContent value="escenas">
          {scenesLoading ? <LoadingSkeleton /> : scenes ? <SceneTable scenes={scenes} /> : null}
        </TabsContent>

        <TabsContent value="personajes">
          {charsLoading ? <LoadingSkeleton /> : characters ? <CharacterList characters={characters} /> : null}
        </TabsContent>

        <TabsContent value="localizaciones">
          {locsLoading ? <LoadingSkeleton /> : locations ? <LocationList locations={locations} /> : null}
        </TabsContent>

        <TabsContent value="narrativa">
          <NarrativeTab jobId={jobId} />
        </TabsContent>

        <TabsContent value="presupuesto">
          <BudgetTab jobId={jobId} />
        </TabsContent>

        <TabsContent value="distribucion">
          <DistributionTab jobId={jobId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
    </div>
  );
}
